import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const NeutronStar = ({ position, scale }) => {
    const groupRef = useRef();
    const coreRef = useRef();
    const jetsRef = useRef();

    useFrame((state) => {
        const t = state.clock.getElapsedTime();
        
        // 1. Core Pulse
        if (coreRef.current) {
            const scalePulse = 1 + Math.sin(t * 15) * 0.02; 
            coreRef.current.scale.setScalar(scalePulse);
        }
    });

    return (
        <group ref={groupRef} position={position} scale={scale}>
            {/* 1. The Core (Tiny, Super-Dense) */}
            <mesh ref={coreRef}>
                <sphereGeometry args={[0.35, 64, 64]} />
                <meshPhysicalMaterial 
                    color="#e0f2fe"
                    emissive="#38bdf8"
                    emissiveIntensity={15} 
                    roughness={0}
                    metalness={1}
                    toneMapped={false}
                />
                <pointLight distance={5} intensity={5} color="#0ea5e9" decay={2} />
            </mesh>

            {/* 2. Magnetic Field Lines / Atmosphere */}
            <mesh>
                <sphereGeometry args={[0.5, 32, 32]} />
                <meshBasicMaterial 
                    color="#0284c7" 
                    transparent 
                    opacity={0.1} 
                    blending={THREE.AdditiveBlending}
                    side={THREE.BackSide}
                />
            </mesh>

            {/* 3. Pulsar Jets (The "Lighthouse" Beams) */}
            <group ref={jetsRef}>
                {/* Top Jet */}
                <mesh position={[0, 0.75, 0]}> {/* Adjusted position to be shorter */}
                    <cylinderGeometry args={[0.15, 0.04, 1.5, 16, 1, true]} /> 
                    <meshBasicMaterial 
                        color="#bae6fd" 
                        transparent 
                        opacity={0.4} 
                        blending={THREE.AdditiveBlending} 
                        side={THREE.DoubleSide}
                        depthWrite={false}
                    />
                </mesh>

                {/* Bottom Jet */}
                <mesh position={[0, -0.75, 0]} rotation={[Math.PI, 0, 0]}> {/* Adjusted position to be shorter */}
                    <cylinderGeometry args={[0.15, 0.04, 1.5, 16, 1, true]} />
                    <meshBasicMaterial 
                        color="#bae6fd" 
                        transparent 
                        opacity={0.4} 
                        blending={THREE.AdditiveBlending} 
                        side={THREE.DoubleSide}
                        depthWrite={false}
                    />
                </mesh>
            </group>
            
            {/* 4. Accretion/Magnetic Ring - Softer */}
            <mesh rotation={[Math.PI / 2, 0, 0]}>
                <torusGeometry args={[1.2, 0.01, 16, 100]} /> 
                <meshBasicMaterial color="#7dd3fc" transparent opacity={0.2} blending={THREE.AdditiveBlending} />
            </mesh>
             <mesh rotation={[Math.PI / 2.2, 0, 0]}>
                <torusGeometry args={[1.5, 0.005, 16, 100]} /> 
                <meshBasicMaterial color="#0ea5e9" transparent opacity={0.15} blending={THREE.AdditiveBlending} />
            </mesh>

            {/* 5. Space-Time Gravity Well (Einstein Grid) */}
            <group rotation={[Math.PI, 0, 0]} position={[0, 0, -0.5]}> {/* Rotated 180 degrees around X */}
                {/* Upper Funnel (The dip) */}
                <mesh>
                    {/* Cylinder: Top Radius=0.1 (Point), Bottom Radius=4 (Wide), Height=1.5 */}
                    <cylinderGeometry args={[0.1, 4, 1.5, 32, 8, true]} /> 
                    <meshBasicMaterial 
                        color="#404040" 
                        wireframe 
                        transparent 
                        opacity={0.3} 
                    />
                </mesh>
                
                {/* Flat Grid (The surrounding space) */}
                <mesh position={[0, -0.75, 0]} rotation={[0, 0, 0]}>
                     <ringGeometry args={[4, 8, 32, 4]} />
                     <meshBasicMaterial 
                        color="#303030" 
                        wireframe 
                        transparent 
                        opacity={0.15} 
                    />
                </mesh>
            </group>

        </group>
    );
};

export default NeutronStar;